<template>
  <div
    v-if="open"
    class="fixed z-[1260] rounded-2xl border border-[#ff009d49] bg-white/95 shadow-2xl p-4 sm:p-5 overflow-y-auto app-scroll"
    :style="menuStyle"
    @click.stop="closeAllDropdowns"
  >
    <div class="flex items-center justify-between mb-4">
      <h5 class="text-sm font-semibold text-zinc-700">
        Button Settings
      </h5>
      <button
        type="button"
        class="rounded-md p-1 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100"
        @click="$emit('close')"
      >
        <XMarkIcon class="w-4 h-4" />
      </button>
    </div>

    <div class="space-y-5">
      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          On Click
        </p>
        <div class="relative">
          <button
            type="button"
            class="w-full rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 pr-9 text-left text-sm font-medium text-zinc-700 shadow-sm transition-colors hover:bg-white focus:outline-none focus:ring-2 focus:ring-[#ff009d52] focus:border-[#ff009d73]"
            @click.stop="toggleDropdown('action_type')"
          >
            {{ selectedActionTypeLabel }}
          </button>
          <ChevronDownIcon
            class="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 transition-transform duration-150"
            :class="{ 'rotate-180': isDropdownOpen('action_type') }"
          />
          <div
            v-if="isDropdownOpen('action_type')"
            class="absolute left-0 right-0 mt-2 z-40 rounded-xl border border-zinc-200 bg-white/95 backdrop-blur-sm shadow-2xl py-1 max-h-52 overflow-y-auto app-scroll"
            @click.stop
          >
            <button
              v-for="option in actionTypeOptions"
              :key="option.value"
              type="button"
              class="w-full text-left px-3 py-2 text-sm transition-colors"
              :class="normalizedActionType === option.value
                ? 'bg-[#ff009d14] text-[#ff009dce] font-semibold'
                : 'text-zinc-700 hover:bg-zinc-100'"
              @click.stop="selectActionType(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="normalizedActionType === 'go_to'">
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Destination
        </p>
        <div class="relative">
          <button
            type="button"
            class="w-full rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 pr-9 text-left text-sm font-medium text-zinc-700 shadow-sm transition-colors hover:bg-white focus:outline-none focus:ring-2 focus:ring-[#ff009d52] focus:border-[#ff009d73]"
            @click.stop="toggleDropdown('goto_type')"
          >
            {{ selectedGoToTypeLabel }}
          </button>
          <ChevronDownIcon
            class="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 transition-transform duration-150"
            :class="{ 'rotate-180': isDropdownOpen('goto_type') }"
          />
          <div
            v-if="isDropdownOpen('goto_type')"
            class="absolute left-0 right-0 mt-2 z-40 rounded-xl border border-zinc-200 bg-white/95 backdrop-blur-sm shadow-2xl py-1 max-h-52 overflow-y-auto app-scroll"
            @click.stop
          >
            <button
              v-for="option in goToTypeOptions"
              :key="option.value"
              type="button"
              class="w-full text-left px-3 py-2 text-sm transition-colors"
              :class="normalizedGoToType === option.value
                ? 'bg-[#ff009d14] text-[#ff009dce] font-semibold'
                : 'text-zinc-700 hover:bg-zinc-100'"
              @click.stop="selectGoToType(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="normalizedActionType === 'go_to' && normalizedGoToType === 'custom_link'">
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Link Address
        </p>
        <input
          type="text"
          class="w-full rounded-md border border-zinc-300 px-2.5 py-2 text-sm text-zinc-700 focus:outline-none focus:ring-1 focus:ring-[#ff009d85] focus:border-[#ff009d85]"
          :value="normalizedLinkAddress"
          placeholder="https://example.com"
          @input="onLinkAddressInput"
        >
      </div>

      <div v-if="normalizedActionType === 'go_to' && normalizedGoToType === 'feature_instance'">
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Feature Instance
        </p>
        <div class="relative">
          <button
            type="button"
            class="w-full rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 pr-9 text-left text-sm font-medium text-zinc-700 shadow-sm transition-colors hover:bg-white focus:outline-none focus:ring-2 focus:ring-[#ff009d52] focus:border-[#ff009d73]"
            @click.stop="toggleDropdown('feature_slug')"
          >
            {{ selectedFeatureLabel }}
          </button>
          <ChevronDownIcon
            class="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 transition-transform duration-150"
            :class="{ 'rotate-180': isDropdownOpen('feature_slug') }"
          />
          <div
            v-if="isDropdownOpen('feature_slug')"
            class="absolute left-0 right-0 mt-2 z-40 rounded-xl border border-zinc-200 bg-white/95 backdrop-blur-sm shadow-2xl py-1 max-h-60 overflow-y-auto app-scroll"
            @click.stop
          >
            <button
              v-for="option in featureSelectOptions"
              :key="`feature-option-${option.value || 'empty'}`"
              type="button"
              class="w-full text-left px-3 py-2 text-sm transition-colors"
              :class="normalizedFeatureSlug === option.value
                ? 'bg-[#ff009d14] text-[#ff009dce] font-semibold'
                : 'text-zinc-700 hover:bg-zinc-100'"
              @click.stop="selectFeatureSlug(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="normalizedActionType === 'file_access'"
        class="space-y-3"
      >
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-1">
          File Access
        </p>
        <button
          type="button"
          class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
          @click="$emit('upload-request')"
        >
          Upload File (PDF, TXT, CSV)
        </button>

        <label class="block">
          <span class="mb-1 block text-xs font-semibold uppercase tracking-wide text-zinc-500">Member Access</span>
          <select
            :value="normalizedFileAccessMode"
            class="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-700"
            @change="onFileAccessModeChange"
          >
            <option value="view">View in Platforma only</option>
            <option value="download">Allow file download</option>
          </select>
        </label>

        <div
          v-if="hasFilePreview"
          class="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2"
        >
          <p class="text-sm font-medium text-zinc-700 break-words [overflow-wrap:anywhere]">
            {{ normalizedFilePreview.fileName || 'Uploaded file' }}
          </p>
          <p
            v-if="normalizedFilePreview.fileMimeType"
            class="text-xs text-zinc-500 mt-0.5"
          >
            {{ normalizedFilePreview.fileMimeType }}
          </p>
          <a
            v-if="normalizedFilePreview.fileUrl"
            :href="normalizedFilePreview.fileUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex mt-2 text-xs font-semibold text-[#ff009dce] hover:text-[#ff009db0]"
          >
            Download File
          </a>
        </div>
      </div>

      <label
        v-if="normalizedActionType === 'go_to'"
        class="flex items-center justify-between gap-3 cursor-pointer"
      >
        <span class="text-sm text-zinc-700 font-medium">Open In New Tab</span>
        <input
          type="checkbox"
          class="h-4 w-4 rounded border-zinc-300 text-[#ff009dce] focus:ring-[#ff009d85]"
          :checked="normalizedOpenInNewTab"
          @change="onOpenInNewTabChange"
        >
      </label>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Background Color
        </p>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="h-9 w-9 rounded-md border border-zinc-300 shadow-sm"
            :style="{ backgroundColor: normalizedBackgroundColor }"
            @click="showBackgroundColorPicker = !showBackgroundColorPicker"
          />
          <input
            type="text"
            maxlength="7"
            class="w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm text-zinc-700 uppercase focus:outline-none focus:ring-1 focus:ring-[#ff009d85] focus:border-[#ff009d85]"
            :value="normalizedBackgroundColor"
            @input="onBackgroundColorTextInput"
            @blur="onBackgroundColorTextBlur"
          >
          <button
            type="button"
            class="rounded-md border border-zinc-300 px-2 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50"
            @click="showBackgroundColorPicker = !showBackgroundColorPicker"
          >
            HEX
          </button>
        </div>
        <transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-2"
        >
          <div
            v-if="showBackgroundColorPicker"
            class="relative mt-3"
          >
            <ModernColorPicker
              v-model="backgroundColorModel"
              class="z-50 relative"
            />
          </div>
        </transition>
      </div>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Text Color
        </p>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="h-9 w-9 rounded-md border border-zinc-300 shadow-sm"
            :style="{ backgroundColor: normalizedTextColor }"
            @click="showTextColorPicker = !showTextColorPicker"
          />
          <input
            type="text"
            maxlength="7"
            class="w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm text-zinc-700 uppercase focus:outline-none focus:ring-1 focus:ring-[#ff009d85] focus:border-[#ff009d85]"
            :value="normalizedTextColor"
            @input="onTextColorTextInput"
            @blur="onTextColorTextBlur"
          >
          <button
            type="button"
            class="rounded-md border border-zinc-300 px-2 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50"
            @click="showTextColorPicker = !showTextColorPicker"
          >
            HEX
          </button>
        </div>
        <transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-2"
        >
          <div
            v-if="showTextColorPicker"
            class="relative mt-3"
          >
            <ModernColorPicker
              v-model="textColorModel"
              class="z-50 relative"
            />
          </div>
        </transition>
      </div>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Text Size
        </p>
        <div class="flex items-center gap-3">
          <input
            type="range"
            min="60"
            max="160"
            step="1"
            class="w-full accent-[#ff009dce]"
            :value="normalizedTextSize"
            @input="onTextSizeRangeInput"
          >
          <div class="flex items-center gap-1 shrink-0">
            <input
              type="number"
              min="60"
              max="160"
              step="1"
              class="w-16 rounded-md border border-zinc-300 px-2 py-1 text-sm text-zinc-700 focus:outline-none focus:ring-1 focus:ring-[#ff009d85] focus:border-[#ff009d85]"
              :value="normalizedTextSize"
              @input="onTextSizeNumberInput"
            >
            <span class="text-xs text-zinc-500">%</span>
          </div>
        </div>
      </div>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Text Font Size
        </p>
        <div class="flex items-center gap-3">
          <input
            type="range"
            min="10"
            max="56"
            step="1"
            class="w-full accent-[#ff009dce]"
            :value="normalizedFontSize"
            @input="onFontSizeRangeInput"
          >
          <div class="flex items-center gap-1 shrink-0">
            <input
              type="number"
              min="10"
              max="56"
              step="1"
              class="w-16 rounded-md border border-zinc-300 px-2 py-1 text-sm text-zinc-700 focus:outline-none focus:ring-1 focus:ring-[#ff009d85] focus:border-[#ff009d85]"
              :value="normalizedFontSize"
              @input="onFontSizeNumberInput"
            >
            <span class="text-xs text-zinc-500">px</span>
          </div>
        </div>
      </div>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Width
        </p>
        <div class="flex items-center gap-3">
          <input
            type="range"
            min="20"
            max="100"
            step="1"
            class="w-full accent-[#ff009dce]"
            :value="normalizedWidth"
            @input="onWidthRangeInput"
          >
          <div class="flex items-center gap-1 shrink-0">
            <input
              type="number"
              min="20"
              max="100"
              step="1"
              class="w-16 rounded-md border border-zinc-300 px-2 py-1 text-sm text-zinc-700 focus:outline-none focus:ring-1 focus:ring-[#ff009d85] focus:border-[#ff009d85]"
              :value="normalizedWidth"
              @input="onWidthNumberInput"
            >
            <span class="text-xs text-zinc-500">%</span>
          </div>
        </div>
      </div>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Padding X
        </p>
        <div class="flex items-center gap-3">
          <input
            type="range"
            min="4"
            max="80"
            step="1"
            class="w-full accent-[#ff009dce]"
            :value="normalizedPaddingX"
            @input="onPaddingXRangeInput"
          >
          <div class="flex items-center gap-1 shrink-0">
            <input
              type="number"
              min="4"
              max="80"
              step="1"
              class="w-16 rounded-md border border-zinc-300 px-2 py-1 text-sm text-zinc-700 focus:outline-none focus:ring-1 focus:ring-[#ff009d85] focus:border-[#ff009d85]"
              :value="normalizedPaddingX"
              @input="onPaddingXNumberInput"
            >
            <span class="text-xs text-zinc-500">px</span>
          </div>
        </div>
      </div>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Padding Y
        </p>
        <div class="flex items-center gap-3">
          <input
            type="range"
            min="4"
            max="56"
            step="1"
            class="w-full accent-[#ff009dce]"
            :value="normalizedPaddingY"
            @input="onPaddingYRangeInput"
          >
          <div class="flex items-center gap-1 shrink-0">
            <input
              type="number"
              min="4"
              max="56"
              step="1"
              class="w-16 rounded-md border border-zinc-300 px-2 py-1 text-sm text-zinc-700 focus:outline-none focus:ring-1 focus:ring-[#ff009d85] focus:border-[#ff009d85]"
              :value="normalizedPaddingY"
              @input="onPaddingYNumberInput"
            >
            <span class="text-xs text-zinc-500">px</span>
          </div>
        </div>
      </div>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Alignment
        </p>
        <div class="grid grid-cols-3 gap-2">
          <button
            v-for="option in alignmentOptions"
            :key="option.value"
            type="button"
            class="rounded-md border px-2 py-1.5 text-sm transition-colors"
            :class="normalizedAlign === option.value
              ? 'border-[#ff009dce] bg-[#ff009d14] text-[#ff009dce]'
              : 'border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50'"
            @click="updateSettings({ buttonAlign: option.value })"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import ModernColorPicker from '@/Components/ModernColorPicker.vue';

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  menuStyle: {
    type: Object,
    default: () => ({ top: '0px', left: '0px', width: '320px', height: '360px' }),
  },
  settings: {
    type: Object,
    default: () => ({
      buttonBackgroundColor: '#2D8BFF',
      buttonTextColor: '#FFFFFF',
      buttonTextSize: 100,
      buttonFontSize: 18,
      buttonActionType: 'go_to',
      buttonGoToType: 'custom_link',
      buttonFeatureSlug: '',
      buttonLinkAddress: '',
      buttonOpenInNewTab: true,
      buttonFileId: null,
      buttonFileUrl: '',
      buttonFileName: '',
      buttonFileMimeType: '',
      buttonFileAccessMode: 'view',
      buttonWidth: 56,
      buttonPaddingX: 24,
      buttonPaddingY: 14,
      buttonAlign: 'center',
    }),
  },
  featureOptions: {
    type: Array,
    default: () => [],
  },
  filePreview: {
    type: Object,
    default: () => ({
      fileId: null,
      fileName: '',
      fileMimeType: '',
      fileUrl: '',
    }),
  },
});

const emit = defineEmits(['update:settings', 'close', 'upload-request']);

const clamp = (value, min, max, fallback) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.min(max, Math.max(min, Math.round(num)));
};
const normalizeColor = (value, fallback) => {
  const raw = String(value || '').trim();
  const withHash = raw.startsWith('#') ? raw : `#${raw}`;
  if (/^#[0-9a-fA-F]{6}$/.test(withHash)) return withHash.toUpperCase();
  return fallback;
};
const normalizeAlign = (value) => {
  const v = String(value || '').toLowerCase();
  if (v === 'left' || v === 'center' || v === 'right') return v;
  return 'center';
};
const normalizeBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  const raw = String(value || '').trim().toLowerCase();
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
};
const normalizeActionType = (value) => {
  const v = String(value || '').trim().toLowerCase();
  if (v === 'go_to' || v === 'file_access' || v === 'open_pay_click_pop') return v;
  return 'go_to';
};
const normalizeGoToType = (value) => {
  const v = String(value || '').trim().toLowerCase();
  if (v === 'custom_link' || v === 'feature_instance') return v;
  return 'custom_link';
};
const normalizeFeatureSlug = (value) => String(value || '').trim();
const normalizeFileName = (value) => String(value || '').trim();
const normalizeFileMimeType = (value) => String(value || '').trim().toLowerCase();
const normalizeFileUrl = (value) => String(value || '').trim();

const normalizedBackgroundColor = computed(() =>
  normalizeColor(props.settings?.buttonBackgroundColor, '#2D8BFF')
);
const normalizedTextColor = computed(() =>
  normalizeColor(props.settings?.buttonTextColor, '#FFFFFF')
);
const normalizedTextSize = computed(() =>
  clamp(props.settings?.buttonTextSize, 60, 160, 100)
);
const normalizedFontSize = computed(() =>
  clamp(props.settings?.buttonFontSize, 10, 56, 18)
);
const normalizedActionType = computed(() =>
  normalizeActionType(props.settings?.buttonActionType)
);
const normalizedGoToType = computed(() =>
  normalizeGoToType(props.settings?.buttonGoToType)
);
const normalizedFeatureSlug = computed(() =>
  normalizeFeatureSlug(props.settings?.buttonFeatureSlug)
);
const normalizedLinkAddress = computed(() =>
  String(props.settings?.buttonLinkAddress || '').trim()
);
const normalizedOpenInNewTab = computed(() =>
  normalizeBoolean(props.settings?.buttonOpenInNewTab)
);
const normalizedWidth = computed(() =>
  clamp(props.settings?.buttonWidth, 20, 100, 56)
);
const normalizedPaddingX = computed(() =>
  clamp(props.settings?.buttonPaddingX, 4, 80, 24)
);
const normalizedPaddingY = computed(() =>
  clamp(props.settings?.buttonPaddingY, 4, 56, 14)
);
const normalizedAlign = computed(() =>
  normalizeAlign(props.settings?.buttonAlign)
);
const normalizedFilePreview = computed(() => ({
  fileId: props.filePreview?.fileId ?? null,
  fileName: normalizeFileName(props.filePreview?.fileName),
  fileMimeType: normalizeFileMimeType(props.filePreview?.fileMimeType),
  fileUrl: normalizeFileUrl(props.filePreview?.fileUrl),
}));
const normalizedFileAccessMode = computed(() =>
  String(props.settings?.buttonFileAccessMode || props.settings?.button_file_access_mode || '').trim().toLowerCase() === 'download'
    ? 'download'
    : 'view'
);
const hasFilePreview = computed(() =>
  Boolean(
    normalizedFilePreview.value.fileId ||
    normalizedFilePreview.value.fileName ||
    normalizedFilePreview.value.fileUrl
  )
);
const normalizedFeatureOptions = computed(() =>
  (Array.isArray(props.featureOptions) ? props.featureOptions : [])
    .map((option) => ({
      value: normalizeFeatureSlug(option?.value),
      label: String(option?.label || '').trim() || normalizeFeatureSlug(option?.value),
    }))
    .filter((option) => option.value)
);

const showBackgroundColorPicker = ref(false);
const showTextColorPicker = ref(false);
const openDropdownKey = ref('');

const backgroundColorModel = computed({
  get: () => normalizedBackgroundColor.value,
  set: (value) => updateSettings({ buttonBackgroundColor: value }),
});
const textColorModel = computed({
  get: () => normalizedTextColor.value,
  set: (value) => updateSettings({ buttonTextColor: value }),
});

const alignmentOptions = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];
const actionTypeOptions = [
  { value: 'go_to', label: 'Go To...' },
  { value: 'open_pay_click_pop', label: 'Open Pay Click-Pop' },
  { value: 'file_access', label: 'File Access' },
];
const goToTypeOptions = [
  { value: 'custom_link', label: 'Custom Action Link' },
  { value: 'feature_instance', label: 'Membership Feature Instance' },
];
const featureSelectOptions = computed(() => [
  { value: '', label: 'Select feature instance' },
  ...normalizedFeatureOptions.value,
]);
const selectedActionTypeLabel = computed(() => {
  return actionTypeOptions.find((option) => option.value === normalizedActionType.value)?.label || 'Go To...';
});
const selectedGoToTypeLabel = computed(() => {
  return goToTypeOptions.find((option) => option.value === normalizedGoToType.value)?.label || 'Custom Action Link';
});
const selectedFeatureLabel = computed(() => {
  if (!normalizedFeatureSlug.value) return 'Select feature instance';
  return normalizedFeatureOptions.value.find((option) => option.value === normalizedFeatureSlug.value)?.label || 'Select feature instance';
});
const isDropdownOpen = (key) => openDropdownKey.value === key;
const toggleDropdown = (key) => {
  openDropdownKey.value = openDropdownKey.value === key ? '' : key;
};
const closeAllDropdowns = () => {
  openDropdownKey.value = '';
};

const updateSettings = (patch = {}) => {
  emit('update:settings', {
    buttonBackgroundColor: normalizeColor(
      patch.buttonBackgroundColor ?? normalizedBackgroundColor.value,
      '#2D8BFF'
    ),
    buttonTextColor: normalizeColor(
      patch.buttonTextColor ?? normalizedTextColor.value,
      '#FFFFFF'
    ),
    buttonTextSize: clamp(
      patch.buttonTextSize ?? normalizedTextSize.value,
      60,
      160,
      100
    ),
    buttonFontSize: clamp(
      patch.buttonFontSize ?? normalizedFontSize.value,
      10,
      56,
      18
    ),
    buttonActionType: normalizeActionType(
      patch.buttonActionType ?? normalizedActionType.value
    ),
    buttonGoToType: normalizeGoToType(
      patch.buttonGoToType ?? normalizedGoToType.value
    ),
    buttonFeatureSlug: normalizeFeatureSlug(
      patch.buttonFeatureSlug ?? normalizedFeatureSlug.value
    ),
    buttonLinkAddress: String(
      patch.buttonLinkAddress ?? normalizedLinkAddress.value
    ).trim(),
    buttonOpenInNewTab: normalizeBoolean(
      patch.buttonOpenInNewTab ?? normalizedOpenInNewTab.value
    ),
    buttonFileId: patch.buttonFileId ?? props.settings?.buttonFileId ?? null,
    buttonFileUrl: normalizeFileUrl(
      patch.buttonFileUrl ?? props.settings?.buttonFileUrl ?? ''
    ),
    buttonFileName: normalizeFileName(
      patch.buttonFileName ?? props.settings?.buttonFileName ?? ''
    ),
    buttonFileMimeType: normalizeFileMimeType(
      patch.buttonFileMimeType ?? props.settings?.buttonFileMimeType ?? ''
    ),
    buttonFileAccessMode: String(
      patch.buttonFileAccessMode ?? props.settings?.buttonFileAccessMode ?? props.settings?.button_file_access_mode ?? normalizedFileAccessMode.value
    ).trim().toLowerCase() === 'download' ? 'download' : 'view',
    buttonWidth: clamp(
      patch.buttonWidth ?? normalizedWidth.value,
      20,
      100,
      56
    ),
    buttonPaddingX: clamp(
      patch.buttonPaddingX ?? normalizedPaddingX.value,
      4,
      80,
      24
    ),
    buttonPaddingY: clamp(
      patch.buttonPaddingY ?? normalizedPaddingY.value,
      4,
      56,
      14
    ),
    buttonAlign: normalizeAlign(
      patch.buttonAlign ?? normalizedAlign.value
    ),
  });
};

const selectActionType = (value) => {
  updateSettings({ buttonActionType: value });
  closeAllDropdowns();
};
const selectGoToType = (value) => {
  updateSettings({ buttonGoToType: value });
  closeAllDropdowns();
};
const selectFeatureSlug = (value) => {
  updateSettings({ buttonFeatureSlug: value });
  closeAllDropdowns();
};
const onFileAccessModeChange = (event) => {
  updateSettings({ buttonFileAccessMode: event?.target?.value });
};
const onBackgroundColorTextInput = (event) => {
  const raw = String(event?.target?.value || '').trim();
  const withHash = raw.startsWith('#') ? raw : `#${raw}`;
  if (/^#[0-9a-fA-F]{3,6}$/.test(withHash)) {
    updateSettings({ buttonBackgroundColor: withHash });
  }
};
const onBackgroundColorTextBlur = (event) => {
  updateSettings({ buttonBackgroundColor: event?.target?.value });
};
const onTextColorTextInput = (event) => {
  const raw = String(event?.target?.value || '').trim();
  const withHash = raw.startsWith('#') ? raw : `#${raw}`;
  if (/^#[0-9a-fA-F]{3,6}$/.test(withHash)) {
    updateSettings({ buttonTextColor: withHash });
  }
};
const onTextColorTextBlur = (event) => {
  updateSettings({ buttonTextColor: event?.target?.value });
};
const onTextSizeRangeInput = (event) => {
  updateSettings({ buttonTextSize: event?.target?.value });
};
const onTextSizeNumberInput = (event) => {
  updateSettings({ buttonTextSize: event?.target?.value });
};
const onFontSizeRangeInput = (event) => {
  updateSettings({ buttonFontSize: event?.target?.value });
};
const onFontSizeNumberInput = (event) => {
  updateSettings({ buttonFontSize: event?.target?.value });
};
const onWidthRangeInput = (event) => {
  updateSettings({ buttonWidth: event?.target?.value });
};
const onWidthNumberInput = (event) => {
  updateSettings({ buttonWidth: event?.target?.value });
};
const onPaddingXRangeInput = (event) => {
  updateSettings({ buttonPaddingX: event?.target?.value });
};
const onPaddingXNumberInput = (event) => {
  updateSettings({ buttonPaddingX: event?.target?.value });
};
const onPaddingYRangeInput = (event) => {
  updateSettings({ buttonPaddingY: event?.target?.value });
};
const onPaddingYNumberInput = (event) => {
  updateSettings({ buttonPaddingY: event?.target?.value });
};
const onLinkAddressInput = (event) => {
  updateSettings({ buttonLinkAddress: event?.target?.value });
};
const onOpenInNewTabChange = (event) => {
  updateSettings({ buttonOpenInNewTab: event?.target?.checked });
};
watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) closeAllDropdowns();
  }
);
</script>

<style scoped>
.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  scrollbar-gutter: stable;
}

.app-scroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.app-scroll::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
}

.app-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.app-scroll::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 999px;
}

.app-scroll:hover {
  scrollbar-color: rgba(145, 150, 156, 0.541) transparent;
}

.app-scroll:hover::-webkit-scrollbar-thumb {
  background-color: rgba(140, 143, 148, 0.479);
}

.app-scroll:hover::-webkit-scrollbar-thumb:hover {
  background-color: rgba(203, 213, 225, 0.65);
}
</style>
